import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Alert, Box, Skeleton, Stack, TextField } from "@mui/material";
import axios from "axios";
import useConfig from "../../../../../hooks/useConfig";
import DownloadedClipsTable from "../../../../DownloadTabs/DownloadClipsList/DownloadedClipsTable";
import HttpService from "../../../../../hooks/Https-services";
import { getCurrentAuth } from "../../../../../hooks/http-server";
import { APIRoutes } from "../../../../../utils/constants";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DialogBox from "../../../../../Common/Component/DialogBox";

/*
 * ONE SOURCE, CHOSEN BY THE TENANT.
 *
 * `audienceLayerEnabled` decides where this page reads a fan's downloads, and
 * the two branches never mix.
 *
 * On a tenant with the layer switched on, the layer holds every download made
 * SINCE it was switched on, and that is a narrower claim than "the whole
 * history". Two things make it true going forward. WatchSidebar renders the
 * Audience Layer panel IN PLACE OF VideoPlayerSideBar, and ClipsData, the v1
 * clip cutter that wrote UserVideoClip.Clips, lives inside that legacy
 * sidebar. And Pages/DownloadTabs drops its own Clips tab on the same flag,
 * which was the other surface still calling creteDownloadVideo and spending a
 * credit the layer never saw. Reading the tenant table alongside the layer
 * would double-count every row both sides hold.
 *
 * WHAT IS NOT TRUE: a tenant flipped on AFTER it already had traffic has real
 * UserVideoClip.Clips and UserDownloadClips rows that spent real credits and
 * have no layer row, and they vanish from both the list and the count at the
 * moment of the flip. Nothing in this page can detect that. Backfilling those
 * rows onto the layer is a release prerequisite for any such tenant, and no
 * production tenant is in that position today. The layer's total is therefore
 * a lower bound for a migrated tenant and exact for one enabled from the
 * start.
 *
 * Everywhere else there is no layer to read, so the page reads the tenant's
 * own endpoint exactly as it always has, counting the rows it gets back.
 *
 * When the layer IS on and its call fails, this page says so rather than
 * falling back to the tenant table. That table is not the answer on an enabled
 * tenant, and substituting it would put a different set of rows under a
 * confident count. This page audits a fan's usage against the 20-credit cap,
 * so an error with an explanation beats a plausible wrong list.
 */

// The layer caps `limit` at 50 and defaults to 25. Asking for the cap keeps the
// round trips down for a fan with a long history.
const AUDIENCE_LAYER_PAGE_SIZE = 50;

/*
 * The layer's base url for this build, or "" when it is not configured. Read
 * at call time, not at module load, so a missing variable surfaces as the
 * visible notice below rather than a request against "undefined/...".
 */
const getAudienceLayerApiUrl = () => {
  const configured = import.meta.env.VITE_AUDIENCE_LAYER_API_URL;
  return typeof configured === "string"
    ? configured.trim().replace(/\/+$/, "")
    : "";
};

export default function UserDownloadCredit({
  userDownloadCredit,
  GetUserDownloadCreditByID,
}: any) {
  const configData = useConfig();
  const [expand, setExpand] = React.useState(false);
  const { id }: any = useParams();
  // The same positive signal that already gates the Watch page and the Studio.
  // A tenant with no such key is treated as off, so nothing changes for it.
  const audienceLayerEnabled = configData?.audienceLayerEnabled === true;
  const [clipsData, setClipsData] = React.useState([] as any);
  // null means the layer never answered, so there is no total to show.
  const [audienceLayerTotal, setAudienceLayerTotal] = React.useState<
    number | null
  >(null);
  const [audienceLayerCursor, setAudienceLayerCursor] = React.useState<
    string | null
  >(null);
  const [audienceLayerFailed, setAudienceLayerFailed] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingButton, setIsLoadingButton] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [downloadCredit, setDownloadCredit] = React.useState(
    userDownloadCredit?.ClipsCredit
  );
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handelUpdateCredit = async () => {
    setIsLoadingButton(true);
    if (!downloadCredit) {
      toast.error("Please enter valid value");
      return;
    }
    if (downloadCredit > 20) {
      toast.error("Download credit should be less than 20");
      return;
    }
    try {
      const res = await HttpService.post(
        APIRoutes.UpdateUserDownloadCredit.replace(":id", id),
        {
          credit: downloadCredit,
        }
      );

      toast.success("Credit Updated Successfully");
      GetUserDownloadCreditByID();
      handleClose();
      setIsLoadingButton(false);
    } catch (error: any) {
      setIsLoadingButton(false);
      toast.error(error.response.data.message);
    }
  };
  /*
   * One page of the fan's download history from the layer. The admin's own
   * tenant access token is what the layer authorises with: both services verify
   * against the same platform JWT secret and the layer's admin guard reads the
   * `role` claim the tenant already stamps on every access token, so there is
   * no second login and no service secret here. It goes out on a bare axios
   * call because HttpService is bound to the tenant base url, and because the
   * tenant client's 401 interceptor would try to refresh, and then sign the
   * admin out, over a rejection from a different service.
   *
   * WHICH FAN, AND WHOSE. Only the fan's id goes in the url. The tenant that
   * scopes it is read by the layer off this token's own `tenant_code` claim,
   * never from anything this page sends, which is what keeps two tenants that
   * both have a user 42 apart. Do NOT add a tenant parameter here: a value the
   * client chooses would let one league's admin read another league's fan.
   *
   * The capital B in "Bearer" is load-bearing, and it is this repo's existing
   * convention rather than a quirk of this one call. Every request that goes
   * to the layer capitalises it: see bitMovingPlayer.tsx, which capitalises it
   * for exactly the requests whose url starts with the layer's api url. Every
   * request that goes to the tenant sends a lowercase "bearer" instead. The
   * layer matches `header.startsWith("Bearer ")` exactly, so both routing this
   * through the tenant client and "tidying" it to match the tenant call sites
   * would 401 on every call.
   */
  const fetchAudienceLayerPage = async (cursor: string | null) => {
    const baseUrl = getAudienceLayerApiUrl();
    if (!baseUrl) {
      throw new Error("VITE_AUDIENCE_LAYER_API_URL is not set");
    }
    const auth = getCurrentAuth();
    const res = await axios.get(
      `${baseUrl}${APIRoutes.AudienceLayerAdminUserDownloads.replace(
        ":id",
        id
      )}`,
      {
        params: {
          limit: AUDIENCE_LAYER_PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
        },
        headers: { Authorization: `Bearer ${auth?.AccessToken}` },
      }
    );
    return res?.data;
  };

  const GetUserDownLoadClipsByID = async () => {
    if (!isLoading) {
      return;
    }

    if (audienceLayerEnabled) {
      try {
        const page = await fetchAudienceLayerPage(null);
        const rows = Array.isArray(page?.data) ? page.data : [];
        setClipsData(rows);
        setAudienceLayerTotal(
          typeof page?.total === "number" ? page.total : rows.length
        );
        setAudienceLayerCursor(page?.nextCursor ?? null);
        setAudienceLayerFailed(false);
      } catch (error: any) {
        // No fallback to the tenant table here, on purpose. See the note at
        // the top of this file: a plausible wrong list is worse than an error.
        setClipsData([]);
        setAudienceLayerTotal(null);
        setAudienceLayerCursor(null);
        setAudienceLayerFailed(true);
      }
      setIsLoading(false);
      return;
    }

    // No layer on this tenant, so the tenant's own table is the whole history,
    // read unpaged exactly as this page has always read it.
    try {
      const res = await HttpService.get(
        APIRoutes.GetUserAllDownloadedVideoClipsByUserID.replace(":id", id)
      );
      setClipsData(res?.data?.data ?? []);
      setIsLoading(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? "Could not load this user's clips"
      );
      setIsLoading(false);
    }
  };

  // Called by the table when the reader pages past the rows it holds. Stops on
  // a null nextCursor, which is the layer saying there is nothing after this.
  const loadMoreFromAudienceLayer = async () => {
    if (!audienceLayerCursor || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    try {
      const page = await fetchAudienceLayerPage(audienceLayerCursor);
      const rows = Array.isArray(page?.data) ? page.data : [];
      setClipsData((previous: any[]) => [...previous, ...rows]);
      if (typeof page?.total === "number") {
        setAudienceLayerTotal(page.total);
      }
      setAudienceLayerCursor(page?.nextCursor ?? null);
    } catch (error: any) {
      // The total already on screen stays, because it is the honest number.
      // The notice is what tells the admin the rows below it are short of it.
      setAudienceLayerFailed(true);
      setAudienceLayerCursor(null);
    }
    setIsLoadingMore(false);
  };

  const toggleAccordion = () => {
    setExpand((prev) => (prev = !prev));
    if (!expand) {
      GetUserDownLoadClipsByID();
    }
  };

  /*
   * A SUCCESSFUL EMPTY ANSWER IS NOT A PROVEN EMPTY HISTORY, and on this screen
   * that difference is the whole point.
   *
   * `audienceLayerEnabled` decides where this page READS. What decides where a
   * download is RECORDED is AUDIENCE_LAYER_URL on the tenant's own backend, and
   * the credit is spent either way: createClipDownload calls spendOneCredit
   * whether or not the register step ran. The two switches are independent, and
   * no tracked tenant env file sets the backend one today, so "flag on, backend
   * registering nothing" is a live combination rather than a hypothetical. In
   * it the layer answers 200 with an empty page for a fan it has never seen,
   * nothing throws, no failure notice fires, and this page would otherwise read
   * "0-0 of 0" for somebody with real downloads and real spent credits.
   *
   * So nothing at all is reported as unproven rather than as none. A fan who
   * genuinely has no downloads sees it too, and that is the right way round:
   * the sentence is true of them as well, and the failure worth avoiding here
   * is a page overstating its own certainty about a credit cap.
   */
  const layerAnsweredNothing =
    audienceLayerEnabled &&
    !audienceLayerFailed &&
    clipsData.length === 0 &&
    audienceLayerTotal === 0;

  return (
    <div style={{ marginTop: "16px" }}>
      <Accordion expanded={expand}>
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon onClick={toggleAccordion} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Stack
            sx={{ width: 1 }}
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "normal",
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Tokens Remaining : {userDownloadCredit?.ClipsCredit}
              </Typography>
            </Stack>
            <Box>
              <button
                onClick={(e: any) => {
                  e.preventDefault();
                  handleClickOpen();
                }}
                style={{ fontSize: "14px" }}
                className={configData.buttonCommonClass}
              >
                Update Credit
              </button>
            </Box>
            <DialogBox
              open={open}
              handleClose={handleClose}
              buttonAction={() => handleClose()}
              buttonActionSecondary={() => handelUpdateCredit()}
              butttonTextSecondary={"Update"}
              buttonText={"Cancel"}
              content={
                <Box>
                  <TextField
                    type="tel"
                    size="small"
                    value={downloadCredit}
                    onChange={(e: any) => setDownloadCredit(e.target.value)}
                    id="outlined-basic"
                    label="Update Credit"
                    variant="outlined"
                  />
                </Box>
              }
            />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {isLoading ? (
            <Skeleton variant="rounded" sx={{ p: 2 }} />
          ) : (
            <>
              {audienceLayerFailed && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This user's Audience Layer download history could not be
                  loaded, so the list below is not their usage. This site's own
                  table is deliberately not shown in its place: on a tenant
                  running the layer it holds a different set of rows, and it
                  would read as a complete answer. Reload before judging this
                  user against their credit cap.
                </Alert>
              )}
              {layerAnsweredNothing && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The Audience Layer has no downloads recorded for this user.
                  That is not proof they have none: it is also what you see when
                  this site's backend is not registering downloads with the
                  layer, which is a separate setting from the one that points
                  this page at it. Check AUDIENCE_LAYER_URL on the backend
                  before reading this as their full usage.
                </Alert>
              )}
              <DownloadedClipsTable
                isInsider={true}
                clipsData={clipsData}
                /*
                 * Both undefined on a tenant without the layer, so the table
                 * counts the rows it holds and never asks for more, which is
                 * what this page has always done.
                 */
                total={
                  audienceLayerEnabled ? audienceLayerTotal ?? 0 : undefined
                }
                onLoadMore={
                  audienceLayerEnabled ? loadMoreFromAudienceLayer : undefined
                }
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
